import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';

import cn from 'classnames';
import { Button, CheckBox, TextField } from 'components/UtilElements';
import { AuthAction, AuthActions, AuthorizationResponse } from 'containers/AuthStateManager';
import { Dispatch } from 'redux';
import { PASSWORD_SET_SUCCESS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { setSessionToken } from 'utils/session';

export const ltiSetPassword = async (password: string, ts: boolean, marketing: boolean) => {
  const response = await request<AuthorizationResponse>({
    url: API.LTI_SET_PW,
    method: 'put',
    data: {
      password,
      ts: ts ? 1 : 0,
      marketing: marketing ? 1 : 0,
    },
  });
  setSessionToken(response.authToken);
  return response;
};

interface SetPasswordProps {
  isResetting: boolean;
  accessData: SessionData | null;
  onPasswordSet(): void;
  setAuthState(action: AuthAction): void;
}

const SetPassword: FC<SetPasswordProps> = ({ isResetting, accessData, setAuthState, onPasswordSet }) => {
  const [password, setPassword] = useState('');
  const [ts, setTs] = useState(false);
  const [acceptTs, setAcceptTs] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setError('');
      setAcceptTs(false);

      if (password.trim() === '') {
        return setError("Password can't be empty");
      } else if (password.trim().length < 8) {
        return setError('Minimum 8 characters are required');
      }

      if (!isResetting && !ts) {
        setAcceptTs(true);
        return;
      }

      try {
        setError('');
        setIsLoading(true);

        const response = await ltiSetPassword(password, ts, marketing);

        if (response.hasLmsApiToken) {
          onPasswordSet();
        } else {
          setAuthState({
            type: AuthActions.AUTHORIZE_ACADLY,
            payload: response,
          });
        }
      } catch (e: any) {
        setError(e.response ? e.response.data.message : e.message);
        setIsLoading(false);
      }
    },
    [isResetting, ts, password, marketing, onPasswordSet, setAuthState]
  );

  if (!accessData) return null;

  return (
    <>
      <h2 className='auth__title'>
        {isResetting ? 'Resetting password' : 'Creating a new account - set password'}
      </h2>
      <form className='auth__panel' onSubmit={onSubmit}>
        <p className='auth__message'>Please set a new password</p>
        <TextField
          hasError={!!error}
          selector='auth__input'
          wrapperClassName='auth__input-wrapper'
          placeholder='Type your desired Acadly password here'
          onchange={(e) => setPassword(e.target.value)}
          value={password}
          type='password'
        />
        <div className={cn('auth__error', { shake: !!error })}>{error}</div>
        {!isResetting && (
          <>
            <p className='auth__tnc'>
              <CheckBox isChecked={ts} onToggle={() => setTs(!ts)}>
                I have read and agree with the{' '}
                <a href='https://www.acadly.com/privacy' target='_blank' rel='noopener noreferrer'>
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href='https://www.acadly.com/terms' target='_blank' rel='noopener noreferrer'>
                  Terms of Service
                </a>
              </CheckBox>
            </p>
            <div className={cn('auth__tnc-error', { shake: acceptTs })}>
              {acceptTs ? 'You need to agree with above policies' : ''}
            </div>
            <p className={cn('auth__tnc', 'auth__tnc--with-margin')}>
              <CheckBox isChecked={marketing} onToggle={() => setMarketing(!marketing)}>
                Acadly can send me marketing emails
              </CheckBox>
            </p>
          </>
        )}
        <div
          className={cn(
            'auth__action-container',
            'auth__action-container--horizontal',
            'auth__action-container--right'
          )}
        >
          <Button
            variant='outlined'
            color='info'
            size='large'
            type='submit'
            isLoading={isLoading}
            className={cn('auth__action', 'auth__action--sm')}
            preventMinWidth
          >
            {isResetting ? 'Login' : 'Continue'}
          </Button>
        </div>
        <p className='auth__note'>
          You will be able to log in to Acadly Apps (iOS, Android and Web) and the Acadly LMS Plugin using
          this email address and password
        </p>
      </form>
    </>
  );
};

const mapStateToProps = ({ getAccess }: RootState) => {
  return { accessData: getAccess };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onPasswordSet: () => {
    dispatch({ type: PASSWORD_SET_SUCCESS });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SetPassword);
